import React from "react";
import "./ChessButton.scss";
import useSoundEffect from "../../../hooks/useSoundEffect";

// Props for the ChessButton
// It extends all native <button> props (onClick, type, disabled, etc.)
interface ChessButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    className?: string;
}

// Reusable ChessButton
// This uses forwardRef to expose the native <button> element to parent components (for focus, DOM access, etc.)
const ChessButton = React.forwardRef<HTMLButtonElement, ChessButtonProps>(
    ({ className, children, onClick, onKeyDown, ...rest }, ref) => {
        const { playSelectAudio } = useSoundEffect();

        const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
            const currentEl = e.currentTarget;
            const navRow = currentEl.dataset.navrow;
            const navCol = currentEl.dataset.navcol;

            // Handle vertical navigation
            if (e.key === "ArrowUp" || e.key === "ArrowDown") {
                e.preventDefault();
                const dir = e.key === "ArrowUp" ? -1 : 1;
                const currentRow = Number(navRow ?? "0");
                let nextRow = currentRow + dir;

                // Find the next available element in that direction
                let nextEl: HTMLElement | null = null;
                let attempts = 0;
                while (!nextEl && attempts < 10) {
                    const selector = `[data-navrow="${nextRow}"][data-navcol="${navCol}"]`;
                    nextEl = document.querySelector(selector) as HTMLElement | null;

                    if (!nextEl) {
                        nextRow += dir;
                        attempts++;
                    }
                }

                if (nextEl) {
                    nextEl.focus();
                }
                return;
            }

            // Call parent's onKeyDown if provided
            if (onKeyDown) onKeyDown(e);
        };

        return (
            <button
                onClick={(e) => {
                    playSelectAudio();
                    if (onClick) onClick(e);
                }}
                onKeyDown={handleKeyDown}
                ref={ref}
                className={`chess-button ${className || ""}`}
                {...rest}
            >
                <span className="chess-button-text">{children}</span>
            </button>
        );
    },
);

ChessButton.displayName = "ChessButton"; // for better debugging

export default ChessButton;
